import * as React from "react"
import Button from "@mui/material/Button"
import Grid from "@mui/material/Grid2"
import SearchIcon from '@mui/icons-material/Search';
import AddCircleSharpIcon from '@mui/icons-material/AddCircleSharp';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    DataGrid,
} from "@mui/x-data-grid"
import { AXIOS_API_INSTANCE } from "../Constants"

import UserModal from "./UserModal"
import { send_authorized_request } from "../Constants/cognito"
import onFilterChange from "../Utils/OnFilterChange"
import { SimpleCustomToolbar } from "./CustomToolbar"
import UserAvatar from "./UserAvatar"
import { Box, InputAdornment, TextField, Tooltip, debounce } from "@mui/material"
import DocumentTitle from "../Utils/DocumentTitle";
import { widthChangeEvent } from "../Utils";
import dayjs from "dayjs"


const columns = [
    {
        field: 'image',
        headerName: 'User',
        type: 'actions',
        width: 90,
        editable: false,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
            return <UserAvatar user={params.row} />
        }
    },
    {
        field: "name",
        headerName: "Name",
        minWidth: 100,
        flex: 1,
        editable: false,
        filterable: false,
        valueGetter: (value, row) => `${value}${row.deactivated ? " (deactivated)" : ""}`,
        renderCell: (params) => {
            const needs_new_pw_link = params.row.last_logged_in === null && !params.row.root_admin && dayjs().diff(dayjs.utc(params.row.date_created), 'days') >= 7
            return (
                <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {needs_new_pw_link &&
                        <Tooltip title={`User has not logged in yet and the previously set password has expired. Open to resend the new password link.`}>
                            <InfoOutlinedIcon sx={{ height: '20px', verticalAlign: 'text-bottom' }} color="error" />
                        </Tooltip>
                    }
                    <span>{params.formattedValue}</span>
                </Box>
            )
        }
    },
    {
        field: "email",
        headerName: "Email",
        minWidth: 130,
        flex: 1,
        editable: false,
        filterable: false,
    },
    {
        field: "permissions",
        headerName: "Permissions",
        minWidth: 150,
        flex: 3,
        editable: false,
        filterable: false,
        sortable: false,
        renderCell: (params) => {
            const permission_list = []
            if (params.row.root_admin) { permission_list.push('Root Admin') }
            if (params.row.permissions_user_admin) { permission_list.push('User Admin') }
            if (params.row.permissions_inventory_admin) { permission_list.push('Inventory Admin') }
            if (params.row.permissions_config_admin) { permission_list.push('Configuration Admin') }
            if (params.row.permissions_service_tech) { permission_list.push('Service Technician') }
            if (params.row.permissions_service_writer) { permission_list.push('Service Writer') }
            if (params.row.permissions_service_manager) { permission_list.push('Service Manager') }
            if (params.row.permissions_service_parts_specialist) { permission_list.push('Service Parts Specialist') }
            if (params.row.permissions_checkout_admin) { permission_list.push('Checkout Admin') }
            if (params.row.permissions_checkout_user) { permission_list.push('Checkout User') }
            if (params.row.permissions_reports_user) { permission_list.push('Reports User') }
            return (
                <span>{permission_list.join(', ')}</span>
            )
        }
    },
    {
        field: "date_created",
        headerName: "Date Created",
        type: 'datetime',
        editable: false,
        sortable: true,
        minWidth: 90,
        flex: 1,
        display: 'flex',
        renderCell: ({ value }) => (
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <span>{dayjs.utc(value).local().format("MM/DD/YYYY")}</span>
            </Box>
        ),
    },
    {
        field: "last_logged_in",
        headerName: "Last Login",
        type: 'datetime',
        editable: false,
        sortable: true,
        minWidth: 90,
        flex: 1,
        display: 'flex',
        renderCell: ({ value }) => (
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <span>{value ? dayjs.utc(value).local().format("MM/DD/YYYY") : '-'}</span>
            </Box>
        ),
    },
]

function Users(props) {
    DocumentTitle('Users')
    const [paginationModel, setPaginationModel] = React.useState({
        pageSize: parseInt(localStorage.getItem(`users:pageSize`) || 10),
        page: 0,
    });
    const [rowData, setRowData] = React.useState({ rows: [], total_count: 0 })
    const [sorting, setSorting] = React.useState({ field: 'id', sort: 'asc' })
    const [filtering, setFiltering] = React.useState({})
    const [isLoading, setIsLoading] = React.useState(true)
    const [userModal, setUserModal] = React.useState({
        open: false,
        user: {},
        creatingNewUser: false,
    })
    const [userSearch, setUserSearch] = React.useState('')
    const [windowInnerWidth, setWindowInnerWidth] = React.useState(window.innerWidth)

    const userRowClick = (params) => {
        setUserModal({ user: params.row, open: true, creatingNewUser: false })
    }
    const createUserClick = (params) => {
        setUserModal({ user: {}, open: true, creatingNewUser: true })
    }
    const closeUserModal = (changes) => {
        setUserModal({ ...userModal, open: false })
    }
    const closeUserModalWithChanges = (changes) => {
        setUserModal({ ...userModal, open: false })
        if (changes) {
            getUsers(sorting.field, sorting.sort, filtering, userSearch)
        }
    }

    const setMyPaginationModel = (model) => {
        localStorage.setItem(`users:pageSize`, model.pageSize)
        setPaginationModel(model)
    }
    const updateUserSearch = (event) => {
        setUserSearch(event.target.value.trim())
        if (event.target.value.trim() !== userSearch) {
            debouncedGetUsers(sorting.field, sorting.sort, filtering, event.target.value.trim())
        }
    }

    const getUsers = (sortField, sortDirection, filterVar, userSearch) => {
        setIsLoading(true)
        let my_filters = Object.assign({}, filterVar);
        if (userSearch.length > 0) {
            my_filters['or'] = []
            my_filters['or'].push({ 'name.ilike': userSearch })
            my_filters['or'].push({ 'email.ilike': userSearch })
        }
        send_authorized_request(() => AXIOS_API_INSTANCE.get("users", {
            params: {
                page_num: paginationModel.page,
                page_size: paginationModel.pageSize,
                order_by: sortField,
                order_by_direction: sortDirection,
                filters: JSON.stringify(my_filters),
                include_locations: true,
            },
        }).then((user) => {
            console.log(user)
            setRowData({
                rows: user.data.data,
                total_count: user.data.total_count,
            })
            setIsLoading(false)
        })
        )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedGetUsers = React.useMemo(() => debounce((sortField, sortDirection, filterVar, userSearch) => getUsers(sortField, sortDirection, filterVar, userSearch), 400), []);


    React.useEffect(() => {
        getUsers(sorting.field, sorting.sort, filtering, userSearch)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel, filtering, sorting])

    React.useEffect(() => {
        return widthChangeEvent(setWindowInnerWidth)
    }, [])

    return (
        (<React.Fragment>
            <Grid container space={1} sx={{ width: '100%' }}>
                <Grid size="grow">
                    <Grid container sx={{ padding: "0" }} justifyContent={"space-between"} alignItems={"center"}>
                        <Grid>
                            <TextField
                                autoFocus
                                margin="dense"
                                id="usersearch"
                                label="Search users"
                                type="text"
                                variant="filled"
                                disabled={isLoading}
                                value={userSearch}
                                onChange={updateUserSearch}
                                slotProps={{
                                    input: {
                                        inputProps: { min: 1 },
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                {<SearchIcon />}
                                            </InputAdornment>
                                        ),
                                    }
                                }}
                            />
                        </Grid>
                        <Grid>
                            <Button onClick={createUserClick} variant="contained" color="primary"><AddCircleSharpIcon />  Add User</Button>
                        </Grid>
                    </Grid>
                    <DataGrid
                        key={windowInnerWidth}
                        slots={{
                            toolbar: () => SimpleCustomToolbar(),
                        }}
                        rows={rowData.rows}
                        rowCount={rowData.total_count}
                        columns={columns}
                        disableRowSelectionOnClick
                        experimentalFeatures={{ newEditingApi: true, columnGrouping: true }}
                        autoHeight={true}
                        filterMode={"server"}
                        loading={isLoading}
                        pageSizeOptions={[5, 10, 25, 50, 100]}
                        pagination
                        paginationMode="server"
                        paginationModel={paginationModel}
                        onPaginationModelChange={setMyPaginationModel}
                        onRowClick={userRowClick}
                        onSortModelChange={(params) => {
                            if (params[0] !== undefined) {
                                setSorting(params[0])
                            } else {
                                setSorting({ field: 'id', sort: 'asc' })
                            }
                        }}
                        onFilterModelChange={(params) => { setFiltering(onFilterChange(params)) }}
                        getRowClassName={(params) => params.row.deactivated ? 'user-datagrid-deactivated' : ''}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    // columns set to false are hidden by default
                                    date_created: false,
                                    last_logged_in: false,
                                },
                            },
                        }}
                    />
                    <UserModal
                        open={userModal.open}
                        user={userModal.user || {}}
                        closeModal={closeUserModal}
                        closeModalWithChanges={closeUserModalWithChanges}
                        creatingNewUser={userModal.creatingNewUser}
                    />
                </Grid>
            </Grid>
        </React.Fragment>)
    );
}

export default Users
